import React, { ReactNode, Suspense, useEffect } from 'react';

import { colorsSelector } from '@redux/colorPalette/selectors';
import { publicInteractionSelector } from '@redux/featureFlags/selector';
import { setLayoutMode } from '@redux/layout';
import { LayoutMode } from '@redux/layout/types';
import { pushToWaitingList } from '@redux/player';
import { roomIdSelector } from '@redux/room/selectors';
import { userTypeSelector } from '@redux/user/selectors';
import socket from 'lib/socket/socket';
import { useDispatch, useSelector } from 'react-redux';
import { useBoolean } from 'react-use';

import ControlBar from 'components/b2b/ControlBar';
import SessionEnd from 'pages/b2b/SessionEnd';
import { lazyWithRetry } from 'services/helpers/lazyWithRetry';
import { getSong } from 'services/networking/songs';
import { SongWithSinger } from 'shared-types/song';
import { B2bUserType } from 'shared-types/user';

import { LayoutContainer, NavigationArea, ContentArea, WaitingList } from './Layout.style';
import { Header } from '../Header/Header';
import { Stack, Typography } from '@mui/material';

const AdminPanel = lazyWithRetry(() => import('components/b2b/AdminPanel'));

interface Props {
  children: ReactNode;
}

const Layout: React.FunctionComponent<Props> = ({ children }) => {
  const [isAdminPanelOpen, setIsPanelAdminOpen] = useBoolean(false);
  const colors = useSelector(colorsSelector);
  const userType = useSelector(userTypeSelector);
  const roomId = useSelector(roomIdSelector);
  const publicInteraction = useSelector(publicInteractionSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setLayoutMode(LayoutMode.DEFAULT));
    if (publicInteraction.isActivated || userType === B2bUserType.BAM) {
      if (!socket.connected) {
        socket.connect();
      }

      socket.emit('joinRoom', roomId);

      if (publicInteraction.isActivated) {
        socket.on('addToPlaylist', async (data) => {
          const { songId, singerName } = data;
          const song = await getSong(songId);

          const chosenSong: SongWithSinger = { ...song };
          chosenSong.singerName = singerName;

          dispatch(pushToWaitingList([chosenSong]));
        });
      }
    }
  }, [socket.connected, publicInteraction.isActivated]);

  return (
    <>
      {process.env.NODE_ENV !== 'production' && (
        <Stack sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1, width: '100%', bgcolor: 'orange', opacity: 0.2, alignItems: 'center', padding: 1 }}>
          <Typography textTransform='uppercase'>{process.env.NODE_ENV}</Typography>
        </Stack>
      )}
      <LayoutContainer>
        <Header onLogoLongPress={() => setIsPanelAdminOpen(true)} />
        <SessionEnd
          onLogoLongPress={() => {
            setIsPanelAdminOpen(true);
          }}
        />
        <NavigationArea {...{ colors }}>
          <ContentArea>{children}</ContentArea>
          <WaitingList />
        </NavigationArea>
        <ControlBar />
      </LayoutContainer>
      <Suspense fallback={null}>
        <AdminPanel isOpen={isAdminPanelOpen} onClose={() => setIsPanelAdminOpen(false)} />
      </Suspense>
    </>
  );
};

export default Layout;
